/**
 * /order — the customer-facing menu. Drives item selection and
 * customization; the cart button + drawer live in the global Layout so
 * they follow the customer to /orders and back. Checkout lives at
 * /checkout (Phase 3).
 */
import { useEffect, useMemo, useState } from 'react';
import type { MenuItem } from '@cracked-mug/shared';
import { CategoryNav } from '../components/menu/CategoryNav';
import { MenuGrid } from '../components/menu/MenuGrid';
import { ItemModal } from '../components/menu/ItemModal';
import { MenuSkeleton } from '../components/menu/MenuSkeleton';
import { useMenu } from '../hooks/useMenu';

export function Menu() {
  const { categories, items, isLoading, error } = useMenu();
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [openItem, setOpenItem] = useState<MenuItem | null>(null);

  // Only categories that actually render a section (active AND with at least
  // one item). The nav chips and the active-chip seed derive from this, so we
  // never show a chip whose scroll target doesn't exist (a dead tap) or seed
  // the active chip to an empty/hidden category.
  const visibleCategories = useMemo(
    () => categories.filter((c) => c.isActive && items.some((i) => i.categoryId === c.categoryId)),
    [categories, items],
  );

  useEffect(() => {
    if (activeCategoryId === null && visibleCategories[0]) {
      setActiveCategoryId(visibleCategories[0].categoryId);
    }
  }, [visibleCategories, activeCategoryId]);

  const handleSelectCategory = (categoryId: string): void => {
    setActiveCategoryId(categoryId);
    const el = document.getElementById(`cat-${categoryId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoading) {
    return <MenuSkeleton />;
  }

  if (error !== null) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-6">
        <p className="font-display text-2xl">Couldn&rsquo;t load the menu</p>
        <p className="text-sm text-wca-gray-mid mt-2 max-w-md">{error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-6">
        <p className="font-display text-2xl">The menu is empty</p>
        <p className="text-sm text-wca-gray-mid mt-2 max-w-md">
          An admin can add items from the admin dashboard. If this is your first time setting up the
          project, run <code className="font-mono">pnpm seed</code> from the repo root.
        </p>
      </div>
    );
  }

  return (
    <>
      <CategoryNav
        categories={visibleCategories}
        activeId={activeCategoryId}
        onSelect={handleSelectCategory}
      />
      <MenuGrid categories={visibleCategories} items={items} onSelectItem={setOpenItem} />
      <ItemModal item={openItem} onClose={() => setOpenItem(null)} />
    </>
  );
}
